import { symphonyAbi, symphonyAddress } from "../../common/constants/contract";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { getConfig } from "../constants/config";
import { calculateAmountOutMin } from "../helpers/calculateAmountOutMin";
import { verifyWalletClient } from "../helpers/verifyWalletClient";
import { giveApproval } from "./giveApproval";
import { checkApproval } from "./checkApproval";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTokenOut } from "../helpers/getTokenOut";

/**
 * Executes a token swap transaction using viem.
 * Handles native token, ERC20 token, and cross-token swaps with slippage protection.
 * Simulates the transaction before execution for each swap type.
 *
 * @param {Object} params - Swap parameters
 * @param {Array} params.route - Route information from getRoute
 * @param {boolean} [params.includesNative] - Whether the swap involves native token
 * @param {import('viem').WalletClient} [params.walletClient] - Wallet client for transaction
 * @param {Object} [params.options] - Swap options
 * @param {boolean} [params.options.skipApproval=true] - Skip token approval step
 * @param {boolean} [params.options.skipCheckApproval=false] - Skip approval check
 * @param {Object} [params.slippage] - Slippage configuration
 * @param {number} [params.slippage.slippageAmount=config.slippage] - Slippage tolerance
 * @param {boolean} [params.slippage.isBps=false] - If true, slippage is in basis points
 *
 * @returns {Promise<Object>} Object with swapReceipt and optional approveReceipt
 *
 * @throws {Error} If approval check/transaction fails
 * @throws {Error} If swap simulation fails
 * @throws {Error} If swap transaction fails
 *
 * @example
 * // Example 1: Simple token to token swap
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const txHash = await swap({
 *   route: route,
 *   walletClient: walletClient
 * });
 *
 * @example
 * // Example 2: Native SEI to token swap with custom slippage
 * const txHash = await swap({
 *   route: route,
 *   includesNative: true,
 *   walletClient: walletClient,
 *   slippage: {
 *     slippageAmount: 50, // 0.5%
 *     isBps: true
 *   }
 * });
 *
 * @example
 * // Example 3: Token to native swap with manual approval
 * const txHash = await swap({
 *   route: route,
 *   includesNative: true,
 *   walletClient: walletClient,
 *   options: {
 *     skipApproval: false,
 *     skipCheckApproval: false
 *   }
 * });
 */
export const swap = async ({
  route,
  includesNative,
  walletClient = undefined,
  options = {
    skipApproval: true,
    skipCheckApproval: false,
    wait: 1,
  },
  slippage = {
    slippageAmount: getConfig().slippage,
    isBps: false,
  },
}) => {

  try {
    const {
      publicClient,
      nativeAddress,
      wrappedNativeAddress,
      functionName,
      feeParams,
    } = getConfig();
    let tokenIn = getTokenIn(route);
    let tokenOut = getTokenOut(route);
    let isTokenApproved = options.skipCheckApproval;
    let approvalReceipt = undefined;

    // If native currency included, treat wrapped currency as native
    includesNative
      ? tokenIn === wrappedNativeAddress
        ? (tokenIn = nativeAddress)
        : (tokenOut = nativeAddress)
      : null;

    // Retrieve user wallet address
    const verifiedWalletClient = await verifyWalletClient(walletClient);

    //Check approval
    if (!options.skipCheckApproval) {
      const approvalCheck = await checkApproval({
        variable: route,
        walletClient: verifiedWalletClient,
        includesNative,
      });
      isTokenApproved = approvalCheck;
    }

    //Give approval
    if (!options.skipApproval && !isTokenApproved) {
      const receipt = await giveApproval({
        variable: route,
        walletClient: verifiedWalletClient,
      });

      approvalReceipt = receipt;
    }

    // NATIVE TO TOKEN TRANSACTION
    if (tokenIn === nativeAddress && tokenOut !== nativeAddress) {
      // Simulate the transaction

      const simulation = await publicClient.simulateContract({
        account: verifiedWalletClient.account,
        address: symphonyAddress,
        abi: symphonyAbi,
        functionName: functionName,
        args: [
          route,
          await calculateAmountOutMin({
            variable: route,
            slippageAmount: slippage.slippageAmount,
            options: {
              isBps: slippage.isBps ?? false,
            },
          }),
          false,
          [
            feeParams.paramFee,
            feeParams.feeAddress,
            feeParams.feeSharePercentage,
          ],
        ],
        value: BigInt((await getTotalAmountIn(route)).amountIn.toString()),
      });

      // Execute the transaction
      const transaction = await verifiedWalletClient.writeContract(
        simulation.request
      );
      const receipt = await publicClient.waitForTransactionReceipt({
        confirmations: options.wait,
        hash: transaction,
      });
      return {
        swapReceipt: receipt,
        approveReceipt: approvalReceipt,
      };
    }
    // TOKEN TO NATIVE TRANSACTION
    if (tokenIn !== wrappedNativeAddress && tokenOut === nativeAddress) {
      // Simulate the transaction

      const simulation = await publicClient.simulateContract({
        account: verifiedWalletClient.account,
        address: symphonyAddress,
        abi: symphonyAbi,
        functionName: functionName,
        args: [
          route,
          await calculateAmountOutMin({
            variable: route,
            slippageAmount: slippage.slippageAmount,
            options: {
              isBps: slippage.isBps ?? false,
            },
          }),
          true,
          [
            feeParams.paramFee,
            feeParams.feeAddress,
            feeParams.feeSharePercentage,
          ],
        ],
      });

      // Execute the transaction
      const transaction = await verifiedWalletClient.writeContract(
        simulation.request
      );

      const receipt = await publicClient.waitForTransactionReceipt({
        confirmations: options.wait,
        hash: transaction,
      });

      return {
        swapReceipt: receipt,
        approveReceipt: approvalReceipt,
      };
    }

    // TOKEN TO TOKEN TRANSACTION
    if (tokenIn !== nativeAddress && tokenOut !== nativeAddress) {
      // Simulate the transaction

      const simulation = await publicClient.simulateContract({
        account: verifiedWalletClient.account,
        address: symphonyAddress,
        abi: symphonyAbi,
        functionName: functionName,
        args: [
          route,
          await calculateAmountOutMin({
            variable: route,
            slippageAmount: slippage.slippageAmount,
            options: {
              isBps: slippage.isBps ?? false,
            },
          }),
          false,
          [
            feeParams.paramFee,
            feeParams.feeAddress,
            feeParams.feeSharePercentage,
          ],
        ],
      });

      // Execute the transaction
      const transaction = await verifiedWalletClient.writeContract(
        simulation.request
      );
      const receipt = await publicClient.waitForTransactionReceipt({
        confirmations: options.wait,
        hash: transaction,
      });
      return {
        swapReceipt: receipt,
        approveReceipt: approvalReceipt,
      };
    }
  } catch (error) {
    console.error("Swap failed:", error);
    throw error;
  }
};
